import { useMemo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Button, TableBadge } from '@sib/ui';
import { FilterType } from '@sib/ui';
import { SearchIcon } from '@sib/ui/icons';
import { NO_DATA_PLACEHOLDER } from '@spendbrain/config/constants';
import { DOCUMENTS_DATE_FORMAT } from '@spendbrain/modules/documents/utils';
import { useAppTranslation } from '@spendbrain/modules/vendors/hooks';
import { format } from 'date-fns';

export enum ContractStatus {
  Archived = 'Archived',
  Active = 'Active',
}

enum IsVetted {
  Yes = 'Yes',
  No = 'No',
}

//TODO: remove mocked interfaces after backend integration
export interface ContractDto {
  id: string;
  contractId: string;
  negotationNumber: string;
  properlyName: string;
  vendorName: string;
  isVetted: boolean;
  effectiveDate: string;
  contractStatus: ContractStatus;
}

export const useContractsColumns = () => {
  const { t } = useAppTranslation();

  const columnHelper = createColumnHelper<ContractDto>();

  const isVettedOptions = useContractIsVettedOptions();
  const contractStatusOptions = useContractStatusOptions();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('contractId', {
          header: t('components.contracts.columns.contractId'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => {
            const contractId = info.getValue() || NO_DATA_PLACEHOLDER;

            return <span className="text-primary font-bold">{contractId}</span>;
          },
        }),
        columnHelper.accessor('negotationNumber', {
          header: t('components.contracts.columns.negotationNumber'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('properlyName', {
          header: t('components.contracts.columns.properlyName'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('vendorName', {
          header: t('components.contracts.columns.vendorName'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('isVetted', {
          header: t('components.contracts.columns.isVetted'),
          meta: {
            filter: FilterType.SELECT,
            filterOptions: isVettedOptions,
            className: 'min-w-[150px]',
          },
          cell: info => {
            const value = info.getValue();
            if (value === undefined) return NO_DATA_PLACEHOLDER;

            return value ? t('yes') : t('no');
          },
        }),
        columnHelper.accessor('effectiveDate', {
          header: t('components.contracts.columns.effectiveDate'),
          meta: {
            filter: FilterType.TEXT, // TODO: update to filter type of date after merging date filter component
            className: 'min-w-[150px]',
          },
          cell: info => {
            const value = info.getValue();
            if (!value) return NO_DATA_PLACEHOLDER;

            return format(value, DOCUMENTS_DATE_FORMAT);
          },
        }),
        columnHelper.accessor('contractStatus', {
          header: t('components.contracts.columns.contractStatus'),
          meta: {
            filter: FilterType.SELECT,
            filterOptions: contractStatusOptions,
            className: 'min-w-[150px]',
          },
          cell: info => {
            const value = info.getValue();
            if (!value) return NO_DATA_PLACEHOLDER;

            return <TableBadge>{value}</TableBadge>; // TODO add translation map to contract status
          },
        }),
        {
          id: 'actions',
          header: () => (
            <span className="flex w-full justify-end">{t('more')}</span>
          ),
          enableSorting: false,
          cell: () => (
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" className="text-primary size-6 p-0">
                <SearchIcon />
              </Button>
            </div>
          ),
          meta: {
            className:
              'sticky right-0 z-20 min-w-[90px] !rounded-none group-odd:bg-white group-even:bg-[#F6F8FA] group-hover:bg-[#EDF2F7] transition-all duration-200',
          },
        },
      ] as ColumnDef<ContractDto>[],
    [columnHelper, t, isVettedOptions, contractStatusOptions]
  );

  return columns;
};

const useContractIsVettedOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      { value: IsVetted.Yes, label: t('yes') },
      {
        value: IsVetted.No,
        label: t('no'),
      },
    ],
    [t]
  );
};

const useContractStatusOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      {
        value: ContractStatus.Active,
        label: t('components.contracts.contractStatus.active'),
      },
      {
        value: ContractStatus.Archived,
        label: t('components.contracts.contractStatus.archived'),
      },
    ],
    [t]
  );
};

export const mockContracts: ContractDto[] = [
  {
    id: '1',
    contractId: 'CON-2024-001',
    negotationNumber: 'NEG-2024-001',
    properlyName: 'Downtown Office Building',
    vendorName: 'ABC Cleaning Services',
    isVetted: true,
    effectiveDate: '2024-01-15',
    contractStatus: ContractStatus.Active,
  },
  {
    id: '2',
    contractId: 'CON-2024-002',
    negotationNumber: 'NEG-2024-002',
    properlyName: 'Main Campus Building A',
    vendorName: 'XYZ Security Systems',
    isVetted: true,
    effectiveDate: '2024-02-01',
    contractStatus: ContractStatus.Active,
  },
  {
    id: '3',
    contractId: 'CON-2024-003',
    negotationNumber: 'NEG-2024-003',
    properlyName: 'Warehouse Facility',
    vendorName: 'Industrial Maintenance Co.',
    isVetted: false,
    effectiveDate: '2024-01-30',
    contractStatus: ContractStatus.Active,
  },
  {
    id: '4',
    contractId: 'CON-2023-001',
    negotationNumber: 'NEG-2023-001',
    properlyName: 'Corporate Headquarters',
    vendorName: 'Elite Landscaping',
    isVetted: true,
    effectiveDate: '2023-06-15',
    contractStatus: ContractStatus.Archived,
  },
  {
    id: '5',
    contractId: 'CON-2024-004',
    negotationNumber: 'NEG-2024-004',
    properlyName: 'Research Laboratory',
    vendorName: 'Scientific Equipment Supply',
    isVetted: true,
    effectiveDate: '2024-03-01',
    contractStatus: ContractStatus.Active,
  },
  {
    id: '6',
    contractId: 'CON-2024-005',
    negotationNumber: 'NEG-2024-005',
    properlyName: 'Data Center',
    vendorName: 'Tech Infrastructure Solutions',
    isVetted: false,
    effectiveDate: '2024-02-15',
    contractStatus: ContractStatus.Active,
  },
  {
    id: '7',
    contractId: 'CON-2023-002',
    negotationNumber: 'NEG-2023-002',
    properlyName: 'Manufacturing Plant',
    vendorName: 'Heavy Machinery Corp',
    isVetted: true,
    effectiveDate: '2023-09-01',
    contractStatus: ContractStatus.Archived,
  },
  {
    id: '8',
    contractId: 'CON-2024-006',
    negotationNumber: 'NEG-2024-006',
    properlyName: 'Retail Store Location',
    vendorName: 'Retail Services Plus',
    isVetted: true,
    effectiveDate: '2024-01-01',
    contractStatus: ContractStatus.Active,
  },
  {
    id: '9',
    contractId: 'CON-2024-007',
    negotationNumber: 'NEG-2024-007',
    properlyName: 'Medical Center',
    vendorName: 'Healthcare Facilities Management',
    isVetted: true,
    effectiveDate: '2024-02-20',
    contractStatus: ContractStatus.Active,
  },
  {
    id: '10',
    contractId: 'CON-2024-008',
    negotationNumber: 'NEG-2024-008',
    properlyName: 'Distribution Center',
    vendorName: 'Logistics Partners Inc',
    isVetted: false,
    effectiveDate: '2024-03-15',
    contractStatus: ContractStatus.Active,
  },
];
